import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Button, ButtonGroup, CircularProgress } from "@material-ui/core";
import api from "../../services/api";
import './styles.scss';

type Period = 'hour' | 'day' | 'week';

interface ServerDataPoint {
    timestamp: string;
    activeSessions: number;
}

interface ServerData {
    serverId: string;
    hostname: string;
    data: ServerDataPoint[];
}

interface DashboardResponse {
    period: string;
    startDate: string;
    endDate: string;
    servers: ServerData[];
}

interface ChartDataPoint {
    timestamp: string;
    [key: string]: string | number; // server hostnames as keys with activeSessions as values
}

export const MetricGraphsCard = () => {
    const [period, setPeriod] = useState<Period>('hour');
    const [loading, setLoading] = useState<boolean>(false);
    const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
    const [servers, setServers] = useState<ServerData[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [hiddenServers, setHiddenServers] = useState<{ [key: string]: boolean }>({});

    // Predefined colors for different servers
    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#a4de6c', '#d0ed57', '#ffc0cb'];

    useEffect(() => {
        fetchData();
    }, [period]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get(`/server-metrics/dashboard?period=${period}`);
            if (response.ok) {
                const data: DashboardResponse = await response.json();
                setServers(data.servers);
                transformData(data.servers);
            } else {
                setError('Erro ao carregar dados do dashboard');
            }
        } catch (err) {
            setError('Erro ao conectar com o servidor');
            console.error('Error fetching dashboard data:', err);
        } finally {
            setLoading(false);
        }
    };

    const transformData = (serversData: ServerData[]) => {
        // Create a map of timestamps to combine all server data
        const timestampMap = new Map<string, ChartDataPoint>();

        serversData.forEach(server => {
            server.data.forEach(point => {
                const timestamp = formatTimestamp(point.timestamp);
                if (!timestampMap.has(timestamp)) {
                    timestampMap.set(timestamp, { timestamp });
                }
                const dataPoint = timestampMap.get(timestamp)!;
                dataPoint[server.hostname] = point.activeSessions;
            });
        });

        const transformed = Array.from(timestampMap.values()).sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        
        setChartData(transformed);
    };

    const formatTimestamp = (timestamp: string): string => {
        const date = new Date(timestamp);
        // Add 3 hours to convert from UTC to local time (UTC-3)
        date.setHours(date.getHours() + 3);
        
        if (period === 'hour') {
            return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        } else if (period === 'day') {
            return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit' });
        } else {
            return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
        }
    };

    const getPeriodLabel = (p: Period): string => {
        const labels = {
            hour: 'Última Hora',
            day: 'Último Dia',
            week: 'Última Semana'
        };
        return labels[p];
    };

    const handleLegendClick = (data: any) => {
        const { dataKey } = data;
        setHiddenServers(prev => ({
            ...prev,
            [dataKey]: !prev[dataKey]
        }));
    };

    return (
        <div className="card grafanaCard">
            <h1>Gráficos de Métricas</h1>
            
            <div className="periodSelector">
                <ButtonGroup variant="contained" color="primary">
                    <Button 
                        onClick={() => setPeriod('hour')}
                        variant={period === 'hour' ? 'contained' : 'outlined'}
                    >
                        Última Hora
                    </Button>
                    <Button 
                        onClick={() => setPeriod('day')}
                        variant={period === 'day' ? 'contained' : 'outlined'}
                    >
                        Último Dia
                    </Button>
                    <Button 
                        onClick={() => setPeriod('week')}
                        variant={period === 'week' ? 'contained' : 'outlined'}
                    >
                        Última Semana
                    </Button>
                </ButtonGroup>
            </div>

            <div className="chartContainer">
                {loading ? (
                  <div className="loadingContainer">
                    <CircularProgress />
                  </div>
                ) : error ? (
                  <div className="errorContainer">
                    <p>{error}</p>
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="noDataContainer">
                    <p>Nenhum dado disponível para o período selecionado</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                      data={chartData}
                      margin={{ top: 15, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timestamp" />
                      <YAxis 
                        label={{ value: 'Sessões Ativas', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip />
                      <Legend 
                        onClick={handleLegendClick}
                        wrapperStyle={{ cursor: 'pointer' }}
                      />
                      {servers.map((server, index) => (
                        <Line
                          key={server.serverId}
                          type="monotone"
                          dataKey={server.hostname}
                          stroke={colors[index % colors.length]}
                          activeDot={{ r: 8 }}
                          name={server.hostname}
                          hide={hiddenServers[server.hostname]}
                          strokeOpacity={hiddenServers[server.hostname] ? 0 : 1}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};